import React, { useState } from 'react';
import { fabric } from 'fabric';
import { useTranslation } from 'react-i18next';
import {
    Circle,
    Eye,
    EyeOff,
    Group as GroupIcon,
    Image as ImageIcon,
    Lock,
    LockOpen,
    Minus,
    Shapes,
    Sparkle,
    Square,
    Type as TypeIcon,
    Wallpaper,
} from 'lucide-react';

import { cn } from '@/lib/utils';

import { isImageObject, isTextObject } from '../fabric-setup';
import type { DesignEditorApi } from '../useDesignEditor';
import { SectionLabel } from '../ui';

/**
 * What a layer is called in the list: the name the vendor gave it, else its
 * words (text), else what kind of thing it is.
 */
export function layerLabel(o: fabric.Object, t: (k: string) => string): string {
    const any = o as any;
    if (any.csNameUser && any.csName) return any.csName;
    if (isTextObject(o)) return ((o as fabric.Textbox).text || '').replace(/\s+/g, ' ').slice(0, 32) || t('DESIGN_EDITOR.LAYER_TEXT');
    if (any.csRole === 'background') return t('DESIGN_EDITOR.LAYER_BACKGROUND');
    if (any.csRole === 'logo') return t('DESIGN_EDITOR.LAYER_LOGO');
    if (any.csRole === 'icon') return t('DESIGN_EDITOR.LAYER_ICON');
    if (any.csRole === 'scrim') return t('DESIGN_EDITOR.LAYER_SCRIM');
    if (any.csRole === 'divider') return t('DESIGN_EDITOR.LAYER_DIVIDER');
    if (isImageObject(o)) return t('DESIGN_EDITOR.LAYER_IMAGE');
    if (o.type === 'group') return t('DESIGN_EDITOR.LAYER_GROUP');
    if (any.csShape) return t(`DESIGN_EDITOR.SHAPE_${String(any.csShape).toUpperCase()}`);
    if (o.type === 'circle') return t('DESIGN_EDITOR.SHAPE_CIRCLE');
    if (o.type === 'line') return t('DESIGN_EDITOR.SHAPE_LINE');
    return t('DESIGN_EDITOR.LAYER_SHAPE');
}

function LayerGlyph({ o }: { o: fabric.Object }) {
    const any = o as any;
    if (any.csRole === 'background') return <Wallpaper />;
    if (isTextObject(o)) return <TypeIcon />;
    if (isImageObject(o)) return <ImageIcon />;
    if (any.csRole === 'icon') return <Sparkle />;
    if (o.type === 'group') return <GroupIcon />;
    if (o.type === 'line' || any.csRole === 'divider') return <Minus />;
    if (o.type === 'circle' || o.type === 'ellipse') return <Circle />;
    if (o.type === 'rect') return <Square />;
    return <Shapes />;
}

export const LayersPanel: React.FC<{ ed: DesignEditorApi }> = ({ ed }) => {
    const { t } = useTranslation();
    const [dragId, setDragId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const objs = (ed.canvas?.getObjects() || []).filter((o) => !(o as any).csTemp);
    const rows = [...objs].reverse();
    const selected = new Set(ed.selection);

    const onRowClick = (o: fabric.Object, e: React.MouseEvent) => {
        if ((o as any).csLocked || o.visible === false) return;
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
            const next = selected.has(o) ? ed.selection.filter((x) => x !== o) : [...ed.selection, o];
            ed.select(next);
        } else {
            ed.select(o);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <SectionLabel>{t('DESIGN_EDITOR.LAYERS')}</SectionLabel>
            {rows.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">{t('DESIGN_EDITOR.LAYERS_EMPTY')}</div>}
            <div className="flex flex-col gap-0.5">
                {rows.map((o) => {
                    const any = o as any;
                    const id = any.csId as string;
                    const isSel = selected.has(o);
                    const hidden = o.visible === false;
                    const locked = !!any.csLocked;
                    return (
                        <div
                            key={id}
                            draggable={editing !== id}
                            onDragStart={(e) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; }}
                            onDragOver={(e) => { e.preventDefault(); if (overId !== id) setOverId(id); }}
                            onDragLeave={() => { if (overId === id) setOverId(null); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const from = objs.find((x) => (x as any).csId === dragId);
                                if (from && from !== o) ed.reorder(from, objs.indexOf(o));
                                setDragId(null);
                                setOverId(null);
                            }}
                            onDragEnd={() => { setDragId(null); setOverId(null); }}
                            onClick={(e) => onRowClick(o, e)}
                            onDoubleClick={() => { setEditing(id); setDraft(layerLabel(o, t)); }}
                            className={cn(
                                'group flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-2 text-xs transition-colors',
                                isSel ? 'border-foreground bg-background ring-[0.5px] ring-foreground' : 'border-transparent hover:bg-foreground/[0.045]',
                                overId === id && dragId !== id && 'border-dashed border-foreground/50',
                                dragId === id && 'opacity-40',
                                hidden && 'opacity-50',
                            )}
                        >
                            <span className={cn('flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md bg-foreground/[0.05] text-muted-foreground [&_svg]:size-3.5', isSel && 'text-foreground')}>
                                {isImageObject(o) && (o as any).getSrc?.()
                                    ? <img src={(o as any).getSrc()} alt="" className="size-full object-cover" />
                                    : <LayerGlyph o={o} />}
                            </span>
                            {editing === id ? (
                                <input
                                    autoFocus
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onBlur={() => { ed.rename(o, draft); setEditing(null); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { ed.rename(o, draft); setEditing(null); }
                                        if (e.key === 'Escape') setEditing(null);
                                    }}
                                    className="h-6 min-w-0 flex-1 rounded border border-foreground/30 bg-background px-1.5 text-xs text-foreground outline-none"
                                />
                            ) : (
                                <span className="min-w-0 flex-1 truncate text-foreground">{layerLabel(o, t)}</span>
                            )}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); ed.toggleVisible(o); }}
                                title={hidden ? t('DESIGN_EDITOR.SHOW') : t('DESIGN_EDITOR.HIDE')}
                                aria-label={hidden ? t('DESIGN_EDITOR.SHOW') : t('DESIGN_EDITOR.HIDE')}
                                className={cn('flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground [&_svg]:size-3.5', !hidden && 'opacity-0 group-hover:opacity-100')}
                            >
                                {hidden ? <EyeOff /> : <Eye />}
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); ed.toggleLock(o); }}
                                title={locked ? t('DESIGN_EDITOR.UNLOCK') : t('DESIGN_EDITOR.LOCK')}
                                aria-label={locked ? t('DESIGN_EDITOR.UNLOCK') : t('DESIGN_EDITOR.LOCK')}
                                className={cn('flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground [&_svg]:size-3.5', !locked && 'opacity-0 group-hover:opacity-100')}
                            >
                                {locked ? <Lock /> : <LockOpen />}
                            </button>
                        </div>
                    );
                })}
            </div>
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{t('DESIGN_EDITOR.LAYERS_HINT')}</p>
        </div>
    );
};
